import L from "leaflet";

/**
 * Escapes HTML special characters in a string.
 */
export function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return c;
    }
  });
}

/**
 * Generates a CSS text-shadow string for outlining text with a given color.
 */
export function outlineShadows(color: string) {
  return [
    `0 1px 0 ${color}`,
    `1px 0 0 ${color}`,
    `0 -1px 0 ${color}`,
    `-1px 0 0 ${color}`,
    `1px 1px 0 ${color}`,
    `-1px 1px 0 ${color}`,
    `1px -1px 0 ${color}`,
    `-1px -1px 0 ${color}`,
  ].join(", ");
}

/**
 * Creates a Leaflet DivIcon with an SVG circle and optional image/label.
 * When `highlighted` is true, adds a coloured outer ring and increases the icon size.
 */
export function makeSvgDivIcon(
  color: string,
  size: number,
  innerImageUrl?: string,
  label?: string,
  highlighted?: boolean,
  highlightColor?: string
) {
  // Extra padding around the circle for the highlight ring
  const pad = highlighted ? 7 : 0;
  const totalSize = size + pad * 2;
  const cx = totalSize / 2;
  const cy = totalSize / 2;

  const innerSize = Math.round(size * 0.5);
  const offset = Math.round((totalSize - innerSize) / 2);

  const imageTag = innerImageUrl
    ? `<image href="${innerImageUrl}" x="${offset}" y="${offset}" width="${innerSize}" height="${innerSize}" preserveAspectRatio="xMidYMid meet" />`
    : "";

  const ringHtml = highlighted
    ? `<circle cx="${cx}" cy="${cy}" r="${totalSize / 2 - 2}" fill="none" stroke="${highlightColor || "#FFD700"}" stroke-width="3.5" stroke-dasharray="5 3" />`
    : "";

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${totalSize}" height="${totalSize}" viewBox="0 0 ${totalSize} ${totalSize}">
      ${ringHtml}
      <circle cx="${cx}" cy="${cy}" r="${size / 2 - 2}" fill="${color}" stroke="white" stroke-width="2" />
      ${imageTag}
    </svg>`;

  const labelHtml = label
    ? `<div style="
          margin-top:4px;
          white-space:nowrap;
          color:#fff;
          font-size:12px;
          line-height:1;
          text-align:center;
          text-shadow:${outlineShadows(color)};
          font-family: veterantypewriter, serif !important;
        ">${escapeHtml(label)}</div>`
    : "";

  const html = `<div style="display:flex; flex-direction:column; align-items:center;">${svg}${labelHtml}</div>`;

  const iconHeight = totalSize + (label ? 18 : 0);
  const iconWidth = Math.max(totalSize, label ? label.length * 7 : totalSize);

  return L.divIcon({
    html,
    className: "",
    iconSize: [iconWidth, iconHeight],
    iconAnchor: [iconWidth / 2, totalSize / 2],
  });
}

/**
 * Creates a cluster icon as a Leaflet DivIcon.
 * Supports multi-color pie slices if colors is an array.
 */
export function makeClusterDivIcon(
  count: number,
  colors: string | string[],
  baseSize: number
) {
  const s = count < 100 ? baseSize + 8 : count < 1000 ? baseSize + 14 : baseSize + 20;
  let svg: string;

  if (Array.isArray(colors)) {
    // Multi-color pie slices
    const slices = colors.length;
    const radius = s / 2 - 2;
    let svgSlices = "";
    for (let i = 0; i < slices; ++i) {
      const startAngle = (i / slices) * 2 * Math.PI;
      const endAngle = ((i + 1) / slices) * 2 * Math.PI;
      const x1 = s / 2 + radius * Math.cos(startAngle);
      const y1 = s / 2 + radius * Math.sin(startAngle);
      const x2 = s / 2 + radius * Math.cos(endAngle);
      const y2 = s / 2 + radius * Math.sin(endAngle);
      const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
      svgSlices += `<path d="M${s / 2},${s / 2} L${x1},${y1} A${radius},${radius} 0 ${largeArc},1 ${x2},${y2} Z" fill="${colors[i]}" />`;
    }
    svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}">
        ${svgSlices}
        <circle cx="${s / 2}" cy="${s / 2}" r="${radius}" fill="none" stroke="white" stroke-width="2" />
        <text x="50%" y="50%" dy="0.35em" text-anchor="middle"
              font-family="system-ui, -apple-system, Segoe UI, Roboto, Arial"
              font-size="${Math.max(10, Math.round(s * 0.4))}" fill="#fff">
          ${count}
        </text>
      </svg>`;
  } else {
    // Single color
    svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}">
        <circle cx="${s / 2}" cy="${s / 2}" r="${s / 2 - 2}" fill="${colors}" />
        <text x="50%" y="50%" dy="0.35em" text-anchor="middle"
              font-family="system-ui, -apple-system, Segoe UI, Roboto, Arial"
              font-size="${Math.max(10, Math.round(s * 0.4))}" fill="#fff">
          ${count}
        </text>
      </svg>`;
  }

  return L.divIcon({
    html: svg,
    className: "cluster-high-z",
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
  });
}

/**
 * Marker kind/type for victim markers.
 */
export type MarkerKind = "missing" | "murder" | "unsolved" | "survived" | "custom" | undefined;

// Cache for marker icons to avoid redundant SVG generation
const iconCache = new Map<string, L.DivIcon>();

/**
 * Returns a cached marker icon or creates a new one if not cached.
 */
export function getMarkerIcon(
  basePath: string,
  color: string,
  kind: MarkerKind,
  label: string | undefined,
  size: number,
  highlighted?: boolean,
  highlightColor?: string
) {
  const img = kind ? `${basePath}/${kind}.png` : `${basePath}/unknown.png`;
  const key = `${color}|${size}|${img}|${label || ""}|${highlighted ? `h:${highlightColor}` : ""}`;
  let icon = iconCache.get(key);
  if (!icon) {
    icon = makeSvgDivIcon(color, size, img, label, highlighted, highlightColor);
    iconCache.set(key, icon);
  }
  return icon;
}